import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import ProjectListAsana from '../components/projects/ProjectListAsana';
import {
    Plus, Search, FolderKanban, Users, Clock, Link2,
    Target, CheckCircle2, Calendar, Archive,
    ChevronRight, ExternalLink, Trash2, Edit, Upload, Download, Paperclip,
    LayoutGrid, List, Sparkles
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const ProjectsPage = () => {
    const { api, user } = useAuth();
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [allTasks, setAllTasks] = useState([]);
    const [projectTasks, setProjectTasks] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedProject, setSelectedProject] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [taskModalProjectId, setTaskModalProjectId] = useState(null);
    const [isEditingProject, setIsEditingProject] = useState(false);
    const [editProjectData, setEditProjectData] = useState(null);
    const [activeTab, setActiveTab] = useState('active');
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [useNewView, setUseNewView] = useState(false);
    const [selectedAsanaProject, setSelectedAsanaProject] = useState(null);
    const [editFromAsana, setEditFromAsana] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        customer_name: '',
        description: '',
        primary_color: '#009dde',
        secondary_color: '#f5f5f7',
        deadline: '',
        team_members: []
    });
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const canManageProject = user?.role === 'chef' || user?.role === 'account_manager';

    // Load view preference from API on mount
    useEffect(() => {
        const loadPreferences = async () => {
            try {
                const res = await api.get('/users/me/preferences');
                if (res.data?.projects_view) {
                    setViewMode(res.data.projects_view);
                }
                if (res.data?.use_new_project_view) {
                    setUseNewView(true);
                }
            } catch (e) {}
        };
        loadPreferences();
    }, [api]);

    // Save view preference when changed
    const handleViewChange = async (newView) => {
        setViewMode(newView);
        try {
            await api.post('/users/me/preferences', { projects_view: newView });
        } catch (e) {}
    };

    // Toggle new Asana-style view
    const handleToggleNewView = async () => {
        const newValue = !useNewView;
        setUseNewView(newValue);
        try {
            await api.post('/users/me/preferences', { use_new_project_view: newValue });
        } catch (e) {}
    };

    // Handle milestone toggle from Asana view
    const handleMilestoneToggle = async (projectId, milestoneId, completed) => {
        try {
            await api.put(`/projects/${projectId}/milestones/${milestoneId}`, { completed });
            fetchProjects();
            if (completed) {
                toast.success('Meilenstein abgeschlossen!');
                confetti({ particleCount: 50, spread: 60, origin: { y: 0.7 } });
            }
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
        }
    };

    const startEditingProject = (project) => {
        setEditProjectData({
            name: project.name || '',
            customer_name: project.customer_name || '',
            description: project.description || '',
            primary_color: project.primary_color || '#009dde',
            secondary_color: project.secondary_color || '#f5f5f7',
            deadline: project.deadline?.split('T')[0] || '',
            team_members: project.team_members || []
        });
        setIsEditingProject(true);
    };

    const cancelEditingProject = () => {
        setEditProjectData(null);
        setIsEditingProject(false);
        // If editing from Asana view, close the modal completely
        if (editFromAsana) {
            setSelectedProject(null);
            setEditFromAsana(false);
        }
    };

    const saveProjectEdit = async () => {
        if (!selectedProject || !editProjectData) return;
        try {
            await api.put(`/projects/${selectedProject.id}`, editProjectData);
            toast.success('Projekt aktualisiert');
            const res = await api.get(`/projects/${selectedProject.id}`);
            setIsEditingProject(false);
            setEditProjectData(null);
            // If editing from Asana view, close modal and stay in Asana view
            if (editFromAsana) {
                setSelectedProject(null);
                setEditFromAsana(false);
            } else {
                setSelectedProject(res.data);
            }
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
        }
    };

    const fetchData = async () => {
        try {
            const [projectsRes, usersRes, tasksRes] = await Promise.all([
                api.get('/projects'),
                api.get('/users'),
                api.get('/tasks')
            ]);
            setProjects(projectsRes.data);
            setUsers(usersRes.data);
            setAllTasks(tasksRes.data);
            
            // Group tasks by project_id
            const tasksByProject = {};
            tasksRes.data.forEach(task => {
                if (task.project_id) {
                    if (!tasksByProject[task.project_id]) {
                        tasksByProject[task.project_id] = [];
                    }
                    tasksByProject[task.project_id].push(task);
                }
            });
            setProjectTasks(tasksByProject);
        } catch (error) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle task toggle from Asana view
    const handleTaskToggle = async (taskId, newStatus) => {
        try {
            await api.put(`/tasks/${taskId}`, { status: newStatus });
            fetchData();
            if (newStatus === 'done') {
                toast.success('Task erledigt!');
                confetti({ particleCount: 50, spread: 60, origin: { y: 0.7 } });
            }
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
        }
    };

    // Handle create task from project
    const handleCreateTaskFromProject = (projectId) => {
        setTaskModalProjectId(projectId);
        setShowTaskModal(true);
    };

    // Handle open task in full modal
    const handleOpenTask = (task) => {
        // Navigate to tasks page with the task selected
        window.location.href = `/tasks?task=${task.id}`;
    };

    // Handle edit project from Asana view
    const handleEditProjectFromAsana = (project) => {
        setEditFromAsana(true);
        setSelectedProject(project);
        startEditingProject(project);
    };

    const handleCreateProject = async () => {
        try {
            await api.post('/projects', formData);
            toast.success('Projekt erstellt');
            setShowCreateModal(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Erstellen');
        }
    };

    const handleUpdateProject = async (updates) => {
        if (!selectedProject) return;
        try {
            await api.put(`/projects/${selectedProject.id}`, updates);
            
            if (updates.status === 'completed') {
                confetti({
                    particleCount: 150,
                    spread: 100,
                    origin: { y: 0.5 },
                    colors: ['#34c759', '#009dde', '#ffcc00', '#ff3b30', '#5856d6']
                });
                toast.success('🎊 Projekt abgeschlossen!');
            }
            
            fetchData();
            const res = await api.get(`/projects/${selectedProject.id}`);
            setSelectedProject(res.data);
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedProject) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Datei zu groß (max. 5MB)');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            await api.post(`/projects/${selectedProject.id}/files`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Datei hochgeladen');
            const res = await api.get(`/projects/${selectedProject.id}`);
            setSelectedProject(res.data);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Hochladen');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm('Datei wirklich löschen?')) return;
        try {
            await api.delete(`/projects/${selectedProject.id}/files/${fileId}`);
            toast.success('Datei gelöscht');
            const res = await api.get(`/projects/${selectedProject.id}`);
            setSelectedProject(res.data);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            customer_name: '',
            description: '',
            primary_color: '#009dde',
            secondary_color: '#f5f5f7',
            deadline: '',
            team_members: []
        });
    };

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    };

    const filteredProjects = projects.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.customer_name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTab = activeTab === 'active' ? p.status !== 'archived' : p.status === 'archived';
        return matchesSearch && matchesTab;
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8" data-testid="projects-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        📁 Projekte
                    </h1>
                    <p className="text-muted-foreground">{filteredProjects.length} Projekte</p>
                </div>
                <div className="flex items-center gap-3">
                    {/* New View Toggle Button with Glow Effect */}
                    <Button
                        variant={useNewView ? "default" : "outline"}
                        size="sm"
                        onClick={handleToggleNewView}
                        className={`relative transition-all duration-300 ${useNewView 
                            ? 'bg-gradient-to-r from-primary to-primary/80 shadow-lg shadow-primary/30' 
                            : 'hover:border-primary/50 hover:shadow-md hover:shadow-primary/20'
                        }`}
                        data-testid="toggle-new-project-view"
                    >
                        {useNewView && (
                            <span className="absolute inset-0 rounded-md bg-primary/20 animate-pulse" />
                        )}
                        <Sparkles className={`w-4 h-4 mr-2 ${useNewView ? 'text-primary-foreground' : 'text-primary'}`} />
                        <span className="relative">{useNewView ? 'Klassische Ansicht' : 'Neue Ansicht'}</span>
                    </Button>
                    {/* View Toggle - Hidden in new view */}
                    {!useNewView && (
                    <div className="flex items-center border border-border rounded-lg overflow-hidden">
                        <button
                            onClick={() => handleViewChange('grid')}
                            className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                            title="Kachelansicht"
                            data-testid="projects-view-grid-btn"
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => handleViewChange('list')}
                            className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                            title="Listenansicht"
                            data-testid="projects-view-list-btn"
                        >
                            <List className="w-4 h-4" />
                        </button>
                    </div>
                    )}
                    {canManageProject && (
                        <Button onClick={() => { resetForm(); setShowCreateModal(true); }} data-testid="create-project-button">
                            <Plus className="w-4 h-4 mr-2" />
                            Neues Projekt
                        </Button>
                    )}
                </div>
            </div>

            {/* Filters - Hidden in new view */}
            {!useNewView && (
            <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Projekte suchen..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="project-search"
                    />
                </div>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="active" data-testid="tab-active">Aktiv</TabsTrigger>
                        <TabsTrigger value="archived" data-testid="tab-archived">Archiv</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>
            )}

            {/* NEW: Asana-style View */}
            {useNewView && (
                <ProjectListAsana
                    projects={filteredProjects}
                    users={users}
                    projectTasks={projectTasks}
                    onProjectSelect={(project) => {
                        if (project) {
                            setSelectedAsanaProject(project.id);
                        } else {
                            setSelectedAsanaProject(null);
                        }
                    }}
                    onEditProject={handleEditProjectFromAsana}
                    onTaskToggle={handleTaskToggle}
                    onCreateTask={handleCreateTaskFromProject}
                    onOpenTask={handleOpenTask}
                    selectedProjectId={selectedAsanaProject}
                />
            )}

            {/* Projects Grid/List View */}
            {!useNewView && viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredProjects.map(project => (
                        <motion.div
                            key={project.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            whileHover={{ y: -4 }}
                            className="cursor-pointer"
                            onClick={() => setSelectedProject(project)}
                            data-testid={`project-card-${project.id}`}
                        >
                            <Card className="shadow-soft hover:shadow-floating transition-all overflow-hidden">
                                {/* Project Header with CI Color */}
                                <div 
                                    className="h-20 relative"
                                    style={{ backgroundColor: project.primary_color }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                                    {project.logo_url && (
                                        <img 
                                            src={project.logo_url} 
                                            alt={project.name}
                                            className="absolute bottom-2 left-4 w-10 h-10 rounded-lg object-cover bg-white"
                                        />
                                    )}
                                </div>
                                <CardContent className="pt-4">
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <h3 className="font-semibold">{project.name}</h3>
                                            <p className="text-sm text-muted-foreground">{project.customer_name}</p>
                                        </div>
                                        <Badge variant={project.status === 'archived' ? 'secondary' : 'default'} className="text-xs">
                                            {project.status === 'archived' ? '📦 Archiviert' : '✨ Aktiv'}
                                        </Badge>
                                    </div>

                                    {/* Progress */}
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between text-sm mb-1">
                                            <span className="text-muted-foreground">Fortschritt</span>
                                            <span className="font-medium">{project.progress}%</span>
                                        </div>
                                        <Progress value={project.progress} className="h-2" />
                                    </div>

                                    {/* Meta */}
                                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                                        <div className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {formatDuration(project.total_time_seconds)}
                                        </div>
                                        <div className="flex -space-x-2">
                                            {project.team_member_details?.slice(0, 3).map(member => (
                                                <Avatar key={member.id} className="w-6 h-6 border-2 border-card">
                                                    <AvatarFallback 
                                                        style={{ backgroundColor: member.profile_color }}
                                                        className="text-white text-[10px]"
                                                    >
                                                        {member.first_name?.[0]}{member.last_name?.[0]}
                                                    </AvatarFallback>
                                                </Avatar>
                                            ))}
                                            {(project.team_member_details?.length || 0) > 3 && (
                                                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] border-2 border-card">
                                                    +{project.team_member_details.length - 3}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            ) : !useNewView ? (
                /* List View */
                <div className="space-y-2">
                    {filteredProjects.map((project, index) => (
                        <motion.div
                            key={project.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                        >
                            <Card
                                className="cursor-pointer hover:shadow-md transition-all group"
                                onClick={() => setSelectedProject(project)}
                                data-testid={`project-row-${project.id}`}
                            >
                                <CardContent className="p-3 flex items-center gap-4">
                                    {/* Color indicator */}
                                    <div 
                                        className="w-2 h-12 rounded-full shrink-0"
                                        style={{ backgroundColor: project.primary_color }}
                                    />
                                    
                                    {/* Name & Customer */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold truncate group-hover:text-primary transition-colors">
                                            {project.name}
                                        </h3>
                                        <p className="text-sm text-muted-foreground truncate">{project.customer_name}</p>
                                    </div>
                                    
                                    {/* Status Badge */}
                                    <Badge variant={project.status === 'archived' ? 'secondary' : 'default'} className="text-xs shrink-0">
                                        {project.status === 'archived' ? '📦' : '✨'} {project.status === 'archived' ? 'Archiviert' : 'Aktiv'}
                                    </Badge>
                                    
                                    {/* Progress */}
                                    <div className="hidden md:flex items-center gap-2 shrink-0 w-32">
                                        <Progress value={project.progress} className="h-2 flex-1" />
                                        <span className="text-sm font-medium w-10 text-right">{project.progress}%</span>
                                    </div>
                                    
                                    {/* Time */}
                                    <div className="hidden md:flex items-center gap-1 text-sm text-muted-foreground shrink-0">
                                        <Clock className="w-4 h-4" />
                                        {formatDuration(project.total_time_seconds)}
                                    </div>
                                    
                                    {/* Team */}
                                    <div className="flex -space-x-2 shrink-0">
                                        {project.team_member_details?.slice(0, 3).map(member => (
                                            <Avatar key={member.id} className="w-6 h-6 border-2 border-card">
                                                <AvatarFallback 
                                                    style={{ backgroundColor: member.profile_color }}
                                                    className="text-white text-[10px]"
                                                >
                                                    {member.first_name?.[0]}{member.last_name?.[0]}
                                                </AvatarFallback>
                                            </Avatar>
                                        ))}
                                        {(project.team_member_details?.length || 0) > 3 && (
                                            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] border-2 border-card">
                                                +{project.team_member_details.length - 3}
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            ) : null}

            {filteredProjects.length === 0 && !useNewView && (
                <div className="text-center py-12 text-muted-foreground">
                    <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Keine Projekte gefunden</p>
                </div>
            )}

            {/* Create Project Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="max-w-lg" data-testid="create-project-modal">
                    <DialogHeader>
                        <DialogTitle>Neues Projekt erstellen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Projektname *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="z.B. Website Redesign"
                                data-testid="project-name-input"
                            />
                        </div>
                        <div>
                            <Label>Kundenname *</Label>
                            <Input
                                value={formData.customer_name}
                                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                                placeholder="z.B. Musterfirma GmbH"
                                data-testid="project-customer-input"
                            />
                        </div>
                        <div>
                            <Label>Beschreibung</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Projektbeschreibung..."
                                data-testid="project-description-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Primärfarbe (CI)</Label>
                                <div className="flex gap-2">
                                    <Input
                                        type="color"
                                        value={formData.primary_color}
                                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                                        className="w-12 h-10 p-1 cursor-pointer"
                                    />
                                    <Input
                                        value={formData.primary_color}
                                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                                        placeholder="#009dde"
                                        data-testid="project-color-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Deadline</Label>
                                <Input
                                    type="date"
                                    value={formData.deadline}
                                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                                    data-testid="project-deadline-input"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Teammitglieder</Label>
                            <Select
                                value=""
                                onValueChange={(v) => {
                                    if (v && !formData.team_members.includes(v)) {
                                        setFormData({ ...formData, team_members: [...formData.team_members, v] });
                                    }
                                }}
                            >
                                <SelectTrigger data-testid="project-members-select">
                                    <SelectValue placeholder="Mitglieder hinzufügen" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.filter(u => !formData.team_members.includes(u.id)).map(u => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {formData.team_members.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {formData.team_members.map(id => {
                                        const u = users.find(u => u.id === id);
                                        return u && (
                                            <Badge key={id} variant="secondary" className="gap-1">
                                                {u.first_name} {u.last_name}
                                                <button onClick={() => setFormData({
                                                    ...formData,
                                                    team_members: formData.team_members.filter(m => m !== id)
                                                })}>×</button>
                                            </Badge>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateModal(false)}>Abbrechen</Button>
                        <Button 
                            onClick={handleCreateProject} 
                            disabled={!formData.name || !formData.customer_name}
                            data-testid="submit-project-button"
                        >
                            Erstellen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Project Detail Modal */}
            <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="project-detail-modal">
                    {selectedProject && (
                        <>
                            {/* Header with CI */}
                            <div 
                                className="h-24 -mx-6 -mt-6 mb-4 relative rounded-t-lg"
                                style={{ backgroundColor: isEditingProject ? (editProjectData?.primary_color || '#009dde') : selectedProject.primary_color }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                <div className="absolute bottom-4 left-6 text-white">
                                    <h2 className="text-2xl font-semibold">{selectedProject.name}</h2>
                                    <p className="opacity-80">{selectedProject.customer_name}</p>
                                </div>
                                {/* Edit Button */}
                                {canManageProject && !isEditingProject && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="absolute top-4 right-12 text-white hover:bg-white/20"
                                        onClick={() => startEditingProject(selectedProject)}
                                        title="Projekt bearbeiten"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>

                            {isEditingProject && editProjectData ? (
                                /* Edit Mode */
                                <div className="space-y-4 mb-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label>Projektname *</Label>
                                            <Input
                                                value={editProjectData.name}
                                                onChange={(e) => setEditProjectData({...editProjectData, name: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <Label>Kundenname *</Label>
                                            <Input
                                                value={editProjectData.customer_name}
                                                onChange={(e) => setEditProjectData({...editProjectData, customer_name: e.target.value})}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <Label>Beschreibung</Label>
                                            <Textarea
                                                value={editProjectData.description}
                                                onChange={(e) => setEditProjectData({...editProjectData, description: e.target.value})}
                                                rows={2}
                                            />
                                        </div>
                                        <div>
                                            <Label>Primärfarbe</Label>
                                            <div className="flex gap-2 mt-1">
                                                <input
                                                    type="color"
                                                    value={editProjectData.primary_color}
                                                    onChange={(e) => setEditProjectData({...editProjectData, primary_color: e.target.value})}
                                                    className="w-10 h-10 rounded cursor-pointer"
                                                />
                                                <Input
                                                    value={editProjectData.primary_color}
                                                    onChange={(e) => setEditProjectData({...editProjectData, primary_color: e.target.value})}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label>Deadline</Label>
                                            <Input
                                                type="date"
                                                value={editProjectData.deadline}
                                                onChange={(e) => setEditProjectData({...editProjectData, deadline: e.target.value})}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <Label>Team</Label>
                                            <Select
                                                value=""
                                                onValueChange={(v) => {
                                                    if (v && !editProjectData.team_members.includes(v)) {
                                                        setEditProjectData({...editProjectData, team_members: [...editProjectData.team_members, v]});
                                                    }
                                                }}
                                            >
                                                <SelectTrigger className="mt-1">
                                                    <SelectValue placeholder="Teammitglied hinzufügen..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {users.filter(u => !editProjectData.team_members.includes(u.id)).map(u => (
                                                        <SelectItem key={u.id} value={u.id}>
                                                            {u.first_name} {u.last_name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {editProjectData.team_members.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {editProjectData.team_members.map(userId => {
                                                        const u = users.find(x => x.id === userId);
                                                        return u ? (
                                                            <Badge key={userId} variant="secondary" className="gap-1">
                                                                {u.first_name} {u.last_name}
                                                                <button onClick={() => setEditProjectData({
                                                                    ...editProjectData,
                                                                    team_members: editProjectData.team_members.filter(id => id !== userId)
                                                                })}>
                                                                    <Trash2 className="w-3 h-3" />
                                                                </button>
                                                            </Badge>
                                                        ) : null;
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2 pt-4 border-t">
                                        <Button variant="outline" onClick={cancelEditingProject}>Abbrechen</Button>
                                        <Button onClick={saveProjectEdit} disabled={!editProjectData.name || !editProjectData.customer_name}>
                                            Speichern
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                /* View Mode */
                                <>
                                    {/* Progress & Stats */}
                                    <div className="grid grid-cols-3 gap-4 mb-6">
                                        <Card>
                                            <CardContent className="pt-4">
                                                <div className="text-2xl font-semibold">{selectedProject.progress}%</div>
                                                <Progress value={selectedProject.progress} className="h-2 mt-2" />
                                                <p className="text-xs text-muted-foreground mt-1">Fortschritt</p>
                                            </CardContent>
                                        </Card>
                                        <Card>
                                            <CardContent className="pt-4">
                                                <div className="text-2xl font-semibold">{formatDuration(selectedProject.total_time_seconds)}</div>
                                                <p className="text-xs text-muted-foreground mt-1">Gesamtzeit</p>
                                            </CardContent>
                                        </Card>
                                        <Card>
                                            <CardContent className="pt-4">
                                                <div className="text-2xl font-semibold">{(projectTasks[selectedProject.id] || []).length}</div>
                                                <p className="text-xs text-muted-foreground mt-1">Meilensteine</p>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* Description */}
                                    {selectedProject.description && (
                                        <div className="mb-6">
                                            <h4 className="text-sm font-medium mb-2">Beschreibung</h4>
                                            <p className="text-muted-foreground">{selectedProject.description}</p>
                                        </div>
                                    )}

                            {/* Team */}
                            <div className="mb-6">
                                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    Team
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {selectedProject.team_member_details?.map(member => (
                                        <div key={member.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                                            <Avatar className="w-6 h-6">
                                                <AvatarFallback style={{ backgroundColor: member.profile_color }} className="text-white text-xs">
                                                    {member.first_name?.[0]}{member.last_name?.[0]}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="text-sm">{member.first_name} {member.last_name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Meilensteine (Tasks verknüpft mit Projekt) */}
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium flex items-center gap-2">
                                        <Target className="w-4 h-4" />
                                        Meilensteine
                                    </h4>
                                    {canManageProject && (
                                        <Button 
                                            size="sm" 
                                            variant="outline" 
                                            onClick={() => {
                                                setTaskModalProjectId(selectedProject.id);
                                                setShowTaskModal(true);
                                            }} 
                                            data-testid="add-milestone-button"
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Task hinzufügen
                                        </Button>
                                    )}
                                </div>
                                
                                <div className="space-y-2">
                                    {(projectTasks[selectedProject.id] || []).map((task, index) => {
                                        const isDone = task.status === 'done' || task.status === 'erledigt';
                                        return (
                                            <motion.div
                                                key={task.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className={`flex items-center justify-between p-3 rounded-xl cursor-pointer hover:bg-muted/70 transition-colors ${isDone ? 'bg-emerald-500/10' : 'bg-muted/50'}`}
                                                onClick={() => handleOpenTask(task)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isDone ? 'bg-emerald-500 text-white' : 'bg-muted'}`}>
                                                        {isDone ? '✓' : index + 1}
                                                    </div>
                                                    <div>
                                                        <p className={`font-medium ${isDone ? 'line-through text-muted-foreground' : ''}`}>
                                                            {task.title}
                                                        </p>
                                                        {task.deadline && (
                                                            <p className="text-xs text-muted-foreground">
                                                                Fällig: {format(new Date(task.deadline), 'd. MMM', { locale: de })}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <Button 
                                                    size="sm" 
                                                    variant={isDone ? "outline" : "default"}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTaskToggle(task.id, isDone ? 'open' : 'done');
                                                    }}
                                                    data-testid={`toggle-task-${task.id}`}
                                                >
                                                    {isDone ? (
                                                        <>🔄 Öffnen</>
                                                    ) : (
                                                        <>
                                                            <CheckCircle2 className="w-4 h-4 mr-1" />
                                                            Erledigt
                                                        </>
                                                    )}
                                                </Button>
                                            </motion.div>
                                        );
                                    })}
                                    {(!projectTasks[selectedProject.id] || projectTasks[selectedProject.id].length === 0) && (
                                        <p className="text-center text-muted-foreground py-4">Noch keine Meilensteine</p>
                                    )}
                                </div>
                            </div>

                            {/* Links */}
                            {selectedProject.links?.length > 0 && (
                                <div className="mb-6">
                                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                        <Link2 className="w-4 h-4" />
                                        Wichtige Links
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedProject.links.map((link, i) => (
                                            <a
                                                key={i}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-muted/50 text-sm hover:bg-muted transition-colors"
                                            >
                                                {link.title || link.url}
                                                <ExternalLink className="w-3 h-3" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Files */}
                            <div className="mb-6">
                                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Paperclip className="w-4 h-4" />
                                    Dateien ({selectedProject.files?.length || 0})
                                </h4>
                                {selectedProject.files?.length > 0 ? (
                                    <div className="space-y-2">
                                        {selectedProject.files.map((file) => (
                                            <div key={file.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                    <span className="text-sm truncate">{file.name}</span>
                                                    <span className="text-xs text-muted-foreground">
                                                        ({Math.round(file.size / 1024)}KB)
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <a
                                                        href={file.url}
                                                        download={file.name}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1.5 rounded hover:bg-background"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </a>
                                                    {canManageProject && (
                                                        <button
                                                            onClick={() => handleDeleteFile(file.id)}
                                                            className="p-1.5 rounded hover:bg-red-500/20 text-red-500"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground">Keine Dateien</p>
                                )}
                                {canManageProject && (
                                    <div className="mt-3">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden"
                                            onChange={handleFileUpload}
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                        >
                                            {uploading ? (
                                                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                                            ) : (
                                                <Upload className="w-4 h-4 mr-2" />
                                            )}
                                            Datei hochladen
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <DialogFooter className="gap-2">
                                {selectedProject.status === 'completed' && (
                                    <Button 
                                        variant="outline" 
                                        onClick={() => handleUpdateProject({ status: 'active' })}
                                        data-testid="reopen-project-button"
                                    >
                                        🔄 Wieder öffnen
                                    </Button>
                                )}
                                {canManageProject && selectedProject.status !== 'archived' && selectedProject.status !== 'completed' ? (
                                    <>
                                        <Button 
                                            variant="outline" 
                                            onClick={() => handleUpdateProject({ status: 'archived' })}
                                        >
                                            <Archive className="w-4 h-4 mr-2" />
                                            Archivieren
                                        </Button>
                                        <Button onClick={() => handleUpdateProject({ status: 'completed' })} data-testid="complete-project-button">
                                            <CheckCircle2 className="w-4 h-4 mr-2" />
                                            Abschließen 🎉
                                        </Button>
                                    </>
                                ) : selectedProject.status === 'archived' && canManageProject ? (
                                    <Button variant="outline" onClick={() => handleUpdateProject({ status: 'active' })}>
                                        Reaktivieren
                                    </Button>
                                ) : null}
                            </DialogFooter>
                            </>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Full Task Creation Modal for Projects (Meilensteine) */}
            <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
                <DialogContent className="max-w-lg" data-testid="project-task-modal">
                    <DialogHeader>
                        <DialogTitle>Task hinzufügen</DialogTitle>
                    </DialogHeader>
                    <FullTaskForm
                        projectId={taskModalProjectId}
                        users={users}
                        api={api}
                        onSuccess={() => {
                            setShowTaskModal(false);
                            setTaskModalProjectId(null);
                            fetchData();
                            toast.success('Task erstellt');
                        }}
                        onCancel={() => {
                            setShowTaskModal(false);
                            setTaskModalProjectId(null);
                        }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Full Task Creation Form (same as TasksPage)
const FullTaskForm = ({ projectId, users, customers, api, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        priority: 2,
        deadline: '',
        deadline_time: '',
        assigned_to: [],
        customer_id: '',
        account_manager_id: '',
        estimated_hours: '',
        is_recurring: false,
        recurrence_pattern: 'none',
        category: ''
    });
    const [loading, setLoading] = useState(false);

    // Get account managers
    const accountManagers = users?.filter(u => ['chef', 'admin', 'account_manager'].includes(u.role)) || [];

    const handleSubmit = async () => {
        if (!formData.title.trim()) return;
        setLoading(true);
        try {
            const taskData = {
                ...formData,
                project_id: projectId,
                status: 'open',
                deadline: formData.deadline 
                    ? (formData.deadline_time 
                        ? `${formData.deadline}T${formData.deadline_time}:00` 
                        : formData.deadline)
                    : null,
                estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null
            };
            delete taskData.deadline_time;
            
            await api.post('/tasks', taskData);
            onSuccess();
        } catch (error) {
            toast.error('Fehler beim Erstellen');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* Title */}
            <div>
                <Label>Titel *</Label>
                <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Task-Titel..."
                    data-testid="task-title-input"
                />
            </div>

            {/* Description */}
            <div>
                <Label>Beschreibung</Label>
                <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Details zum Task..."
                    rows={3}
                />
            </div>

            {/* Priority & Status */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>Priorität</Label>
                    <Select
                        value={String(formData.priority)}
                        onValueChange={(v) => setFormData({ ...formData, priority: Number(v) })}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">Hoch</SelectItem>
                            <SelectItem value="2">Mittel</SelectItem>
                            <SelectItem value="3">Niedrig</SelectItem>
                            <SelectItem value="0">Nicht dringend</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Kategorie</Label>
                    <Input
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder="z.B. Design, Development..."
                    />
                </div>
            </div>

            {/* Deadline */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>Fälligkeitsdatum</Label>
                    <Input
                        type="date"
                        value={formData.deadline}
                        onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    />
                </div>
                <div>
                    <Label>Uhrzeit (optional)</Label>
                    <Input
                        type="time"
                        value={formData.deadline_time}
                        onChange={(e) => setFormData({ ...formData, deadline_time: e.target.value })}
                    />
                </div>
            </div>

            {/* Assignment */}
            <div>
                <Label>Zuweisen an</Label>
                <Select
                    value={formData.assigned_to[0] || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, assigned_to: v === 'none' ? [] : [v] })}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Person auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">Niemand</SelectItem>
                        {users?.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                                {user.first_name} {user.last_name} ({user.display_title || user.role})
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Account Manager */}
            <div>
                <Label>Account Manager</Label>
                <Select
                    value={formData.account_manager_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, account_manager_id: v === 'none' ? '' : v })}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Account Manager..." />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">Kein Account Manager</SelectItem>
                        {accountManagers?.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                                {user.first_name} {user.last_name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Estimated Hours */}
            <div>
                <Label>Zeitschätzung (Stunden)</Label>
                <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.estimated_hours}
                    onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                    placeholder="z.B. 2.5"
                />
            </div>

            {/* Recurring */}
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div>
                    <Label className="mb-0">Wiederkehrender Task</Label>
                    <p className="text-xs text-muted-foreground">Task wiederholt sich automatisch</p>
                </div>
                <input
                    type="checkbox"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                    className="w-5 h-5"
                />
            </div>

            {formData.is_recurring && (
                <div>
                    <Label>Wiederholung</Label>
                    <Select
                        value={formData.recurrence_pattern}
                        onValueChange={(v) => setFormData({ ...formData, recurrence_pattern: v })}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="daily">Täglich</SelectItem>
                            <SelectItem value="weekly">Wöchentlich</SelectItem>
                            <SelectItem value="monthly">Monatlich</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}

            <DialogFooter className="pt-4 border-t">
                <Button variant="outline" onClick={onCancel}>Abbrechen</Button>
                <Button onClick={handleSubmit} disabled={!formData.title.trim() || loading} data-testid="submit-task-btn">
                    {loading ? 'Erstelle...' : 'Task erstellen'}
                </Button>
            </DialogFooter>
        </div>
    );
};

export default ProjectsPage;
